import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, CircularProgress, Button, Chip, Tooltip, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { apiService } from "../../services/api";
import { formatFeatureLabel } from "../../utils/formatFeatureLabel";
import SHAPBarChart from "./SHAPBarChart";
import OverviewGaugeChart from "./OverviewGaugeChart";

interface ShipmentSHAPDetailsProps {
  shipmentId: string;
  delayProbability?: number;
  sourceCountry?: string;
  destination?: string;
  supplierName?: string;
}

const TILE_HEIGHT = 320;

const ShipmentSHAPDetails: React.FC<ShipmentSHAPDetailsProps> = ({
  shipmentId,
  delayProbability = 0,
  sourceCountry,
  destination,
  supplierName,
}) => {
  const [loading, setLoading] = useState(true);
  const [explanation, setExplanation] = useState<any>(null);
  const [recommendations, setRecommendations] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [showGraph, setShowGraph] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [shapRes, recRes] = await Promise.allSettled([
          apiService.explainShipment(shipmentId),
          apiService.getAiRecommendations(shipmentId)
        ]);

        if (shapRes.status === "fulfilled") {
          setExplanation(shapRes.value?.data ?? shapRes.value);
        }
        if (recRes.status === "fulfilled") {
          const recData = recRes.value?.data ?? recRes.value;
          if (recData?.recommendations && typeof recData.recommendations === "string") {
            try { recData.recommendations = JSON.parse(recData.recommendations); } catch { /* ignore parse errors */ }
          }
          setRecommendations(recData);
        }
      } catch (err) {
        console.error("Failed to load shipment details", err);
        setError("Failed to load shipment details");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [shipmentId]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>;
  if (error) return <Typography color="error">{error}</Typography>;

  const isAtRisk = delayProbability > 40;
  const routeDisplay = explanation?.source_country && explanation?.destination_city
    ? `${explanation.source_country} → ${explanation.destination_city}`
    : sourceCountry && destination
      ? `${sourceCountry} → ${destination}`
      : "N/A";
  const supplier = explanation?.supplier_name || supplierName || "N/A";

  return (
    <Paper sx={{ p: 2, bgcolor: "#f9fafb", borderRadius: 2 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1.2fr" }, gap: 2, alignItems: "stretch" }}>

        {/* LEFT CARD - Risk Overview (removed risk score and button) */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: "1px solid #e5e7eb", bgcolor: "#ffffff", minHeight: TILE_HEIGHT, display: "flex", flexDirection: "column" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Risk Overview</Typography>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
            <OverviewGaugeChart delayPercentage={delayProbability} />
          </Box>

          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="caption" sx={{ color: '#64748b' }}>Predicted Outcome</Typography>
            <Box sx={{ mt: 1 }}>
              <Chip
                label={isAtRisk ? "At Risk" : "On Time"}
                size="small"
                sx={{
                  bgcolor: isAtRisk ? '#fef2f2' : '#ecfdf5',
                  color: isAtRisk ? '#ef4444' : '#10b981',
                  fontWeight: 600,
                  border: `1px solid ${isAtRisk ? '#fecaca' : '#a7f3d0'}`
                }}
              />
            </Box>
          </Box>

          <Box sx={{ borderTop: '1px solid #e5e7eb', pt: 2, mt: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#64748b' }}>Route:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{routeDisplay}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#64748b' }}>Supplier:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{supplier}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#64748b' }}>Freight Mode:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Sea</Typography>
            </Box>
          </Box>
        </Paper>

        {/* RIGHT CARD - AI Recommendations or SHAP Graph */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: "1px solid #e5e7eb", bgcolor: "#ffffff", minHeight: TILE_HEIGHT, display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>

          {showGraph ? (
            // SHAP Graph View
            <>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Top Risk Drivers (SHAP)</Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                Factors contributing to this shipment risk prediction
              </Typography>

              <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", minWidth: 0, mb: 2 }}>
                <SHAPBarChart
                  topFeatures={(explanation?.top_features ?? explanation?.topFeatures ?? [])}
                  height={Math.max(200, ((explanation?.top_features ?? explanation?.topFeatures ?? []).length) * 35)}
                  showLegend={true}
                />
              </Box>

              <Button
                variant="outlined"
                fullWidth
                onClick={() => setShowGraph(false)}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                Show AI Recommendations
              </Button>
            </>
          ) : (
            // AI Recommendations View
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>AI Recommendations</Typography>
                <Tooltip
                  title={
                    <Box component="span" sx={{ display: 'block', maxWidth: 280 }}>
                      <Box component="span" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>Source</Box>
                      Generated from this shipment&apos;s SHAP risk drivers and historical shipment/route data.
                      <Box component="span" sx={{ display: 'block', fontWeight: 600, mt: 1, mb: 0.5 }}>Methodology</Box>
                      Recommendations are derived by combining top SHAP factors with delay patterns and best practices.
                    </Box>
                  }
                  placement="top"
                  arrow
                  enterTouchDelay={0}
                >
                  <IconButton size="small" sx={{ p: 0.25 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 18, color: '#64748b' }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                Primary Driver: {formatFeatureLabel(recommendations?.primary_driver)}
              </Typography>

              <Box sx={{ flex: 1, overflowY: "auto", mb: 2 }}>
                {(Array.isArray(recommendations?.recommendations) ? recommendations.recommendations : []).map((rec: any, idx: number) => (
                  <Box key={rec.title ?? `rec-${idx}`} sx={{ mb: 2, p: 2, bgcolor: '#f9fafb', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{rec.title}</Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>{rec.why}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>Actions:</Typography>
                    <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                      {rec.actions?.map((action: string, i: number) => (
                        <li key={action}><Typography variant="caption">{action}</Typography></li>
                      ))}
                    </ul>
                  </Box>
                ))}
              </Box>

              <Button
                variant="outlined"
                fullWidth
                onClick={() => setShowGraph(true)}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                Show Factors Graph
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Paper>
  );
};

export default ShipmentSHAPDetails;


